import { scopedTranslation } from '@utils/I18n'
import { Link } from '@atoms'
import PaginationEllipsis from '@atoms/PaginationEllipsis'
import clsx from 'clsx'
import { Link as InertiaLink } from '@inertiajs/react'
import { callingSessionSupporterUrl } from '../../supporters-management/utils/routes'

const t = scopedTranslation('components.supporter_pagination')

type SupporterNavigation = {
  id: string
  position: number
  supporterId: string
}

type SupporterPaginationProps = {
  currentPosition: number
  totalSupporters: number
  callingSessionId: string
  sessionSupporters: SupporterNavigation[]
  only?: string[]
  previousSupporter?: {
    id: string
    supporter: {
      id: string
      firstName: string | null
      lastName: string | null
    }
  } | null
  nextSupporter?: {
    id: string
    supporter: {
      id: string
      firstName: string | null
      lastName: string | null
    }
  } | null
}

export function SupporterPagination({
  currentPosition,
  totalSupporters,
  callingSessionId,
  sessionSupporters,
  only = [],
  previousSupporter,
  nextSupporter,
}: SupporterPaginationProps) {
  const formatSupporterName = (supporter: { firstName: string | null; lastName: string | null }) => {
    return `${supporter.firstName ?? ''} ${supporter.lastName ?? ''}`.trim()
  }
  const supportersMap = new Map(
    sessionSupporters.map((sessionSupporter) => [sessionSupporter.position, sessionSupporter])
  )

  const generateSupporterLink = (position: number): string => {
    const supporter = supportersMap.get(position)
    return supporter ? callingSessionSupporterUrl(callingSessionId, supporter.supporterId) : ''
  }

  type PageItem =
    | {
        type: 'page'
        number: number
      }
    | {
        type: 'ellipsis'
      }

  const generatePageItems = (): PageItem[] => {
    const showAllIfLessThan = 11
    const amountToShowAroundCurrent = 1
    const amountToShowAtEdges = 3
    const showExtraWhenOffByLessThan = 6

    const showAllPages = totalSupporters < showAllIfLessThan
    const currentIsInFirstPages = currentPosition <= showExtraWhenOffByLessThan
    const currentIsInLastPages = currentPosition >= totalSupporters - showExtraWhenOffByLessThan

    const createPageItems = (start: number, end: number): PageItem[] => {
      const items: PageItem[] = []
      for (let i = start; i <= end; i++) {
        items.push({ type: 'page', number: i })
      }
      return items
    }

    if (showAllPages) {
      return createPageItems(1, totalSupporters)
    }

    const items: PageItem[] = []

    if (!currentIsInFirstPages) {
      items.push(...createPageItems(1, amountToShowAtEdges))
      items.push({ type: 'ellipsis' })
    }

    let startValue = currentPosition - amountToShowAroundCurrent
    let endValue = currentPosition + amountToShowAroundCurrent

    if (currentIsInFirstPages) {
      startValue = 1
    } else if (currentIsInLastPages) {
      endValue = totalSupporters
    }

    items.push(...createPageItems(startValue, endValue))

    if (!currentIsInLastPages) {
      items.push({ type: 'ellipsis' })
      items.push(...createPageItems(totalSupporters - amountToShowAtEdges + 1, totalSupporters))
    }

    return items
  }

  const pageItems = generatePageItems()

  const previousItem = previousSupporter
    ? {
        link: callingSessionSupporterUrl(callingSessionId, previousSupporter.supporter.id),
        label: `${t('previous')}: ${formatSupporterName(previousSupporter.supporter)}`,
      }
    : null

  const nextItem = nextSupporter
    ? {
        link: callingSessionSupporterUrl(callingSessionId, nextSupporter.supporter.id),
        label: `${t('next')}: ${formatSupporterName(nextSupporter.supporter)}`,
      }
    : null

  return (
    <div data-testid="supporter-pagination" className="tw:flex tw:items-center tw:w-full">
      <div className="tw:flex-1 tw:flex tw:justify-start">
        <Link
          disabled={!previousItem}
          href={previousItem?.link ?? ''}
          rank="link"
          size="sm"
          leadingIcon="arrow-narrow-left"
          as="inertiaLink"
          only={only}
        >
          {previousItem?.label ?? t('previous')}
        </Link>
      </div>

      <ul className="tw:flex tw:items-center tw:justify-center tw:gap-4">
        {pageItems.map((item, index) =>
          item.type === 'ellipsis' ? (
            <PaginationEllipsis key={`ellipsis-${index}`} />
          ) : (
            <li
              key={item.number}
              className={clsx(
                'tw:w-fit tw:h-fit tw:rounded-full tw:font-bold tw:text-xs',
                'tw:*:inline-flex tw:*:items-center tw:*:justify-center tw:*:w-7 tw:*:h-7 tw:*:rounded-full',
                item.number === currentPosition
                  ? 'tw:bg-primary-700 tw:text-white tw:*:pointer-events-none tw:*:text-white'
                  : 'tw:bg-gray-100 tw:text-black tw:*:text-black'
              )}
            >
              <InertiaLink only={only} href={generateSupporterLink(item.number)}>
                {item.number}
              </InertiaLink>
            </li>
          )
        )}
      </ul>

      <div className="tw:flex-1 tw:flex tw:justify-end">
        <Link
          disabled={!nextItem}
          href={nextItem?.link ?? ''}
          rank="link"
          size="sm"
          trailingIcon="arrow-narrow-right"
          as="inertiaLink"
          only={only}
        >
          {nextItem?.label ?? t('next')}
        </Link>
      </div>
    </div>
  )
}
